import { FileViewer } from "@/components/features/matter/file-repo/FileViewer";
import {
  ActionConfirmationModal,
  AppModal,
  Button,
  DataTable,
  DateFormField,
  LoadingSpinner,
  Typography,
} from "@/components/ui";
import { SelectedFolderOrFile } from "@/components/ui/File/SelectedFolderOrFile";
import { Form } from "@/components/ui/Form/Form";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { useUser } from "@/components/UserProvider";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import { retrieveContactQueryOptions } from "@/lib/data/react-query";
import { useSendTemplatedEmailToUser } from "@/lib/data/react-query/contacts/contact-templated-email";
import { useDeleteFirmSignatureRequest } from "@/lib/data/react-query/firm/firm-signature-request";
import { getFirmSignatureRequestPublicFileUrlV2 } from "@/lib/data/requests/firm/firm-signature-request";
import { retrieveFirmFilesOptions } from "@/lib/data/react-query/firm/firm-file";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import type { FirmFile } from "@/lib/types";
import type { ReportsSignatureRequestReport } from "@/lib/types/Reports";
import { formatUsersName } from "@/lib/utils/format";
import { getFirmFileDisplayName } from "@/lib/utils/firmFile";
import {
  getSignatureRequestReportRowDocumentDisplayName,
  mapSignatureRequestReportRowToListItem,
} from "@/lib/utils/matters/signature-request-report-utils";
import { cn } from "@/lib/utils/cn";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import {
  buildSignatureRequestRecipientRows,
  signatureRequestRecipientColumns,
  type SignatureRequestRecipientRow,
} from "./signatureRequestRecipientsTable";

type FirmSignatureRequestFormValues = {
  firmSignatureRequestExpirationDate: string;
};

interface UpdateFirmSignatureRequestSlideoverProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  title: string;
  description: string;
  reportRow: ReportsSignatureRequestReport;
}

export const UpdateFirmSignatureRequestSlideover = ({
  title,
  open,
  setOpen,
  reportRow,
}: UpdateFirmSignatureRequestSlideoverProps) => {
  const { openCommunicationModal, openSendEmailModal } = useGlobalModal();
  const firmId = reportRow.signatureRequestReportFirmId ?? "";
  const firmFilesQuery = useQuery<
    Awaited<ReturnType<typeof retrieveFirmFilesOptions>> extends infer T
      ? T extends { queryFn: () => Promise<infer R> }
        ? R
        : never
      : never
  >(retrieveFirmFilesOptions(open && Boolean(firmId)));
  const firmFiles = useMemo(
    () => firmFilesQuery.data?.data ?? [],
    [firmFilesQuery.data],
  );

  const { hasActionPermission, currentUser } = useUser();
  // Firm signature requests are immutable after creation in the action catalog.
  const hasUpdatePermission = false;
  const canSendUserEmails = hasActionPermission(
    ACTION_PERMISSION_KEYS.user,
    ACTION_PERMISSION_VALUES.create,
  );
  const deleteFirmSignatureRequest = useDeleteFirmSignatureRequest();
  const sendTemplatedEmailToUserMutation = useSendTemplatedEmailToUser();
  const queryClient = useQueryClient();
  const [isDeleting, setIsDeleting] = useState(false);
  const [emailSent, setEmailSent] = useState<Record<string, boolean>>({});
  const [userEmailSent, setUserEmailSent] = useState<Record<string, boolean>>(
    {},
  );
  const [userEmailSending, setUserEmailSending] = useState<
    Record<string, boolean>
  >({});

  const listItem = useMemo(
    () => mapSignatureRequestReportRowToListItem(reportRow),
    [reportRow],
  );

  const signatureRequestId =
    reportRow.signatureRequestReportSignatureRequestId ?? "";

  const selectedFile = useMemo<FirmFile | null>(() => {
    if (!firmFiles.length) {
      return null;
    }

    const explicitId = reportRow.signatureRequestReportFirmFileId;
    if (explicitId) {
      return firmFiles.find((f) => f.id === explicitId && !f.isFolder) ?? null;
    }

    const displayName =
      getSignatureRequestReportRowDocumentDisplayName(reportRow);
    const byDisplay = firmFiles.find(
      (f) => !f.isFolder && getFirmFileDisplayName(f) === displayName,
    );
    if (byDisplay) {
      return byDisplay;
    }

    return firmFiles.find((f) => !f.isFolder && f.name === displayName) ?? null;
  }, [firmFiles, reportRow]);

  const selectedDestinationFolder = useMemo<FirmFile | null>(() => {
    const destinationId =
      reportRow.signatureRequestReportSignatureRequestFileIdDestination;

    if (!destinationId || !firmFiles.length) {
      return null;
    }

    return firmFiles.find((f) => f.id === destinationId && f.isFolder) ?? null;
  }, [firmFiles, reportRow]);

  const selectedDestinationFolderParentPath = useMemo<string>(() => {
    if (!selectedDestinationFolder) {
      return "";
    }

    const segments: string[] = [];
    let parentId = selectedDestinationFolder.idLocation;

    while (parentId) {
      const parentFolder = firmFiles.find(
        (f) => f.id === parentId && f.isFolder,
      );

      if (!parentFolder) {
        break;
      }

      segments.unshift(parentFolder.name);
      parentId = parentFolder.idLocation;
    }

    return segments.join(" / ");
  }, [firmFiles, selectedDestinationFolder]);

  const contactSigners = useMemo(
    () => listItem?.matterSignatureRequestContactSigners ?? [],
    [listItem],
  );
  const userSigners = useMemo(
    () => listItem?.matterSignatureRequestUserSigners ?? [],
    [listItem],
  );
  const recipients = useMemo(
    () =>
      buildSignatureRequestRecipientRows({
        contactSigners,
        userSigners,
      }),
    [contactSigners, userSigners],
  );
  const signatureRequestFileKey =
    reportRow.signatureRequestReportFirmFilePresignedUrlKey ||
    selectedFile?.presignedUrlKey ||
    "";
  const previewPasscode = recipients.at(0)?.passcode ?? "";
  const signatureRequestDocumentQuery = useQuery({
    queryKey: [
      "firm-signature-request-document",
      signatureRequestId,
      signatureRequestFileKey,
    ],
    queryFn: () =>
      getFirmSignatureRequestPublicFileUrlV2({
        clientMethod: "get_object",
        key: signatureRequestFileKey,
        signatureRequestPasscode: previewPasscode,
      }),
    enabled:
      open && Boolean(signatureRequestFileKey) && Boolean(previewPasscode),
  });
  const signatureRequestDocumentUrl =
    signatureRequestDocumentQuery.data?.data.presignedUrl;

  const form = useForm<FirmSignatureRequestFormValues>({
    values: {
      firmSignatureRequestExpirationDate:
        reportRow.signatureRequestReportSignatureRequestExpirationDate ?? "",
    },
  });

  const isFormLoading = deleteFirmSignatureRequest.isPending;

  const handleDelete = async () => {
    if (!firmId || !signatureRequestId) {
      return;
    }

    setOpen(false);
    await deleteFirmSignatureRequest.mutateAsync({ signatureRequestId });
    setIsDeleting(false);
  };

  const handleOpenTextForSendSignLink = (row: SignatureRequestRecipientRow) => {
    const contactId = row.contactConnection?.contactId;

    if (contactId) {
      void queryClient.prefetchQuery(retrieveContactQueryOptions(contactId));
      openCommunicationModal({
        contactId,
        defaultMessage: `Please review and e-sign your document using this secure link: ${window.location.origin}/sign?passcode=${row.passcode} \n\nIf you didn’t expect this, ignore or contact our office.`,
      });
    }
  };

  const handleSendEmailSignLink = (row: SignatureRequestRecipientRow) => {
    const contactId = row.contactConnection?.contactId;

    if (!contactId || emailSent[contactId]) {
      return;
    }

    const primaryEmail = row.email;

    if (!primaryEmail) {
      toast.error("Contact does not have an email address.");
      return;
    }

    const recipientName = row.displayName;
    const contactIds = [contactId];
    const expirationDate =
      reportRow.signatureRequestReportSignatureRequestExpirationDate || "";
    const signaturePasscode = row.passcode || "";
    const signatureLink = `${window.location.origin}/sign?passcode=${signaturePasscode}`;
    const senderName = formatUsersName(currentUser) || "";
    const firmName = currentUser.firm.name;

    const templateData = {
      document_name: selectedFile?.name ?? "",
      document_id: selectedFile?.id ?? "",
      due_date: expirationDate,
      unique_password: signaturePasscode,
      signature_link: signatureLink,
      expiration_date: expirationDate,
      sender_name: senderName,
      firm_name: firmName,
      recipient_name: recipientName,
    };

    openSendEmailModal({
      title: "Send signature request email",
      description: "Review and send the signature request email.",
      toEmails: [primaryEmail],
      contactIds,
      templateName: "ElectronicSignatureRequestEmailTemplate",
      templateData,
      onSuccess: () => {
        setEmailSent((prev) => ({ ...prev, [contactId]: true }));
      },
    });
  };

  const handleSendEmailUserSignLink = async (
    row: SignatureRequestRecipientRow,
  ) => {
    const id = row.userConnection?.userId;
    const email = row.email;

    if (!id || !email || userEmailSent[id]) {
      if (!email) {
        toast.error("User does not have an email address.");
      }
      return;
    }

    const recipientName = row.displayName || "";
    const expirationDate =
      reportRow.signatureRequestReportSignatureRequestExpirationDate || "";
    const signaturePasscode = row.passcode || "";
    const signatureLink = `${window.location.origin}/sign?passcode=${signaturePasscode}`;
    const senderName = formatUsersName(currentUser) || "";
    const firmName = currentUser.firm.name;

    const templateData = {
      document_name: selectedFile?.name ?? "",
      document_id: selectedFile?.id ?? "",
      due_date: expirationDate,
      unique_password: signaturePasscode,
      signature_link: signatureLink,
      expiration_date: expirationDate,
      sender_name: senderName,
      firm_name: firmName,
      recipient_name: recipientName,
    };

    setUserEmailSending((prev) => ({ ...prev, [id]: true }));

    try {
      await sendTemplatedEmailToUserMutation.mutateAsync({
        userIds: [id],
        toEmails: [email],
        templateName: "FirmElectronicSignatureRequestEmailTemplate",
        templateData,
      });
      setUserEmailSent((prev) => ({ ...prev, [id]: true }));
    } finally {
      setUserEmailSending((prev) => {
        return Object.fromEntries(
          Object.entries(prev).filter(([key]) => key !== id),
        ) as Record<string, boolean>;
      });
    }
  };

  useEffect(() => {
    if (!open) {
      form.reset();
      setEmailSent({});
      setUserEmailSent({});
      setUserEmailSending({});
      setIsDeleting(false);
    }
  }, [open, form]);

  if (open && !firmId) {
    return (
      <AppModal
        open={open}
        onOpenChange={setOpen}
        title={title}
        size="3xl"
        footer={
          <div className="flex justify-end">
            <FormCancelButton setOpen={setOpen} label="Close" />
          </div>
        }
      >
        <Typography className="text-muted-foreground">
          This signature request is missing firm context. Try again after
          refreshing the page.
        </Typography>
      </AppModal>
    );
  }

  if (open && firmId && firmFilesQuery.isPending) {
    return (
      <AppModal open={open} onOpenChange={setOpen} title={title} size="3xl">
        <div className="flex min-h-[min(50vh,480px)] flex-col items-center justify-center gap-3">
          <LoadingSpinner className="h-8 w-8" />
          <Typography className="text-muted-foreground text-sm">
            Loading signature request…
          </Typography>
        </div>
      </AppModal>
    );
  }

  if (open && firmId && firmFilesQuery.isError) {
    return (
      <AppModal
        open={open}
        onOpenChange={setOpen}
        title={title}
        size="3xl"
        footer={
          <div className="flex justify-end">
            <FormCancelButton setOpen={setOpen} label="Close" />
          </div>
        }
      >
        <Typography className="text-muted-foreground">
          Unable to load firm files. Open firm files from settings and try
          again.
        </Typography>
      </AppModal>
    );
  }

  if (!listItem) {
    return (
      <AppModal open={open} onOpenChange={setOpen} title={title} size="3xl">
        <div />
      </AppModal>
    );
  }

  return (
    <AppModal
      open={open}
      onOpenChange={setOpen}
      title={title}
      size="3xl"
      loading={isFormLoading}
      footer={
        <div className="flex w-full items-center">
          {hasActionPermission(
            ACTION_PERMISSION_KEYS.firmSignatureRequest,
            ACTION_PERMISSION_VALUES.delete,
          ) && (
            <Button
              type="button"
              variant="destructive"
              onClick={() => {
                setIsDeleting(true);
              }}
            >
              Delete
            </Button>
          )}
          <div className="ml-auto flex justify-end space-x-2">
            <FormCancelButton setOpen={setOpen} label="Close" />
          </div>
        </div>
      }
    >
      <Form {...form}>
        <div className="space-y-6 pt-2">
          {recipients.length > 0 && (
            <div className="space-y-4">
              <Typography className="text-sm font-medium">
                Recipients
              </Typography>
              <DataTable
                entityLabelPlural="recipients"
                columns={signatureRequestRecipientColumns({
                  expirationDate:
                    reportRow.signatureRequestReportSignatureRequestExpirationDate,
                  showOrderColumn: recipients.some((r) => r.order != null),
                  onSendContactText: handleOpenTextForSendSignLink,
                  onSendContactEmail: handleSendEmailSignLink,
                  onSendUserEmail: handleSendEmailUserSignLink,
                  contactEmailSent: emailSent,
                  userEmailSent,
                  userEmailSending,
                  canSendUserEmails,
                })}
                data={recipients}
                tableName={tanstackTableNames.matterSignatureRequestRecipients}
                onRowClick={() => {}}
                hideSearchBar
                hideVisiibilityDropdown
                noResultsMessage="No recipients assigned"
              />
            </div>
          )}
          {recipients.length === 0 && (
            <Typography className="text-muted-foreground text-sm">
              No recipients have been added yet.
            </Typography>
          )}
          <fieldset
            className={cn(
              slideoverFieldsetClassName,
              "m-0 grid-cols-1 p-0 md:grid-cols-2",
            )}
            disabled
          >
            <div className="col-span-2 grid grid-cols-subgrid gap-4">
              <DateFormField
                label="Expiration date*"
                register={form.register("firmSignatureRequestExpirationDate")}
                name="firmSignatureRequestExpirationDate"
                clearable={false}
              />
            </div>
          </fieldset>
          <fieldset
            className={cn(
              slideoverFieldsetClassName,
              "m-0 grid-cols-1 p-0 md:grid-cols-2",
            )}
            disabled={!hasUpdatePermission}
          >
            <div className="col-span-full">
              <div className="mb-2 text-sm font-medium">Document</div>
              {selectedFile ? (
                <div className="flex items-center justify-between rounded-md border px-3 py-2 text-sm">
                  <span className="truncate">
                    {getFirmFileDisplayName(selectedFile)}
                  </span>
                </div>
              ) : (
                <Typography className="text-muted-foreground text-sm">
                  No document found for this signature request.
                </Typography>
              )}
            </div>
          </fieldset>
          <fieldset
            className={cn(
              slideoverFieldsetClassName,
              "m-0 grid-cols-1 p-0 md:grid-cols-2",
            )}
            disabled={!hasUpdatePermission}
          >
            <div className="col-span-full">
              <div className="mb-2 text-sm font-medium">Destination folder</div>
              {selectedDestinationFolder ? (
                <SelectedFolderOrFile
                  type="Folder"
                  name={selectedDestinationFolder.name}
                  parentPath={selectedDestinationFolderParentPath}
                  canRemove={false}
                />
              ) : (
                <Typography className="text-muted-foreground text-sm">
                  Files will be saved to the root folder.
                </Typography>
              )}
            </div>
          </fieldset>
          {selectedFile ? (
            <div className="bg-muted/30 rounded-md border p-4">
              <FileViewer
                fileType="firm-file"
                file={{
                  fileName: getFirmFileDisplayName(selectedFile),
                  presignedURLKey: signatureRequestFileKey,
                }}
                presignedUrl={signatureRequestDocumentUrl}
                showDownload={false}
                showTitle={false}
                imageClassName="h-full max-h-[800px] w-auto"
              />
            </div>
          ) : null}
        </div>
      </Form>

      <ActionConfirmationModal
        title="Are you sure you want to delete this signature request?"
        open={isDeleting}
        setOpen={setIsDeleting}
        afterActionFn={async () => {
          await handleDelete();
        }}
      />
    </AppModal>
  );
};
